import React, {Component, useMemo, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {ColumnMenuModule, ColumnsToolPanelModule, ContextMenuModule, SetFilterModule,} from "ag-grid-enterprise";
import {AllCommunityModule, ClientSideRowModelModule} from "ag-grid-community";
import {Helmet} from "react-helmet";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import CustomNameCellEditor from "../../utils/customCellEditor";
import DTPicker from "../../utils/DTPicker";
import {
    loadDataWithSSEAndStartChangeStreamListener,
    standardHandlePopulateGrid
} from "../../utils/sseAndChangeStreamHelper";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {MuiIconButtonWithTooltipAndBox} from "../../components/muiComponents";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getItemFromStorageWithoutDecrypting} from "../../utils/storageHelper";
import {defaultClientSideTextFilterParams} from "../../utils/filterHelper";
import {getDefaultAgGridSidebarProps} from "../../utils/gridFilterStateAndColumnStateHelper";
import {useOutletContext} from "react-router-dom";


export default function PartnerPaymentManagement() {
    const { isLoading, setIsLoading } = useOutletContext();
    const [gridApi, setGridApi] = useState(null);
    const [sseDataPullActive, setSSEDataPullActive] = useState(true);
    const [asyncTransactionWaitMillis, setAsyncTransactionWaitMillis] = useState(200); //200 to start for the initial sse data pull, will change when sse data pull is done for change streams
    const [columnDefs, setColumnDefs] = useState([
        {
            field: "username", initialWidth: 350, headerName: "User",
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
            },
            sortable: true,
            editable: false
        },
        {
            field: "type", initialWidth: 300, headerName: "Payment Type",
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
            },
            sortable: true,
            editable: false
        },
        { field: "company", headerName: "Company", initialWidth: 350,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
            },
            sortable: true,
            editable: false
        },
        { field: "last4", headerName: "Last 4 Numbers of Payment Method", initialWidth: 350,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false
        }
    ])
    const [defaultColDef, setDefaultColDef] = useState(
        {
            resizable: true,
            filterParams: null,
            headerClass:"border-0 border-b-0",
            cellClass:"outline:none",
            autoHeight:false,
            enableCellChangeFlash:true,
            floatingFilter:true,
            cellDataType: false //disable inferring cell data type automatically, can be overridden in individual colDef
        }
    )
    const sideBar = useMemo(() => {
        //Inside useMemo to help prevent the sidebar from re-rendering
        return getDefaultAgGridSidebarProps(350)
    }, []);
    const rowSelection = useMemo(() => {
        return {
            mode: 'singleRow',
            enableClickSelection: true,
            checkboxes: false,
            headerCheckbox: false
        };
    }, []);
    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Payment Management</title>
                <script src="https://js.stripe.com/v3/"/>
                <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap" rel="stylesheet"/>
            </Helmet>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-3 h-full">
                    {privatePageHeaderHelper("Payment Management")}
                    <hr className="h-0.5" />
                    <div className="flex flex-row justify-between flex-wrap gap-x-0 gap-y-3">
                        <div className="flex flex-row justify-start gap-x-6 flex-wrap gap-y-2 items-center">
                            <MuiIconButtonWithTooltipAndBox
                                icon={<FontAwesomeIcon className="object-contain" icon="fa-brands fa-stripe-s" size="xs"/>}
                                tooltipTitle={"Manage payments and subscriptions in the Stripe Customer Portal"}
                                tooltipPlacement={"right"}
                                onClick={() => {
                                    let stripeCustomerPortalLink = process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL_LINK
                                    let userName = getItemFromStorageWithoutDecrypting("username")
                                    if(userName !== null && userName !== undefined){
                                        stripeCustomerPortalLink = stripeCustomerPortalLink + "?prefilled_email=" + userName.trim().toLowerCase()
                                    }
                                    window.open(stripeCustomerPortalLink, "_blank", "noreferrer")
                                }}/>
                        </div>
                        <ClearRefresh gridApi = {gridApi} showRefreshIcon={false} />
                    </div>
                    <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                        <Grid
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            sideBar={sideBar}
                            rowSelection={rowSelection}
                            setGridApi={setGridApi}
                            sseDataPullActive={sseDataPullActive}
                            setSSEDataPullActive={setSSEDataPullActive}
                            asyncTransactionWaitMillis={asyncTransactionWaitMillis}
                            setAsyncTransactionWaitMillis={setAsyncTransactionWaitMillis}
                        />
                    </div>
                </div>
        </div>
    );
}

class Grid extends Component {
    rowData = []
    abortController = new AbortController()

    constructor(props, onClickRow, filterVals) {
        super(props);
    }

    onCellEditingStopped = (event) => {
        let gridApi = event.api
        //Nothing to edit on this grid
    }

    componentWillUnmount(){
        this.abortController.abort()
    }

    populateGrid = async (rowData) => {
        standardHandlePopulateGrid(rowData, this.gridApi)
    }

    getRowId = (params) => {
        return params.data.id
    }

    getContextMenuItems = (params) => {
        return [
            "resetColumns",
            "autoSizeAll"
        ];
    };

    onGridReady = async (gridReadyParams) => {
        this.gridApi = gridReadyParams.api;
        this.props.setGridApi(gridReadyParams.api);

        // Disable text selection on the page while holding shift or control (to allow grid selections to be done easily without selecting all text)
        ["keyup","keydown"].forEach((event) => {
            window.addEventListener(event, (e) => {
                document.onselectstart = function() {
                    return !(e.shiftKey || e.ctrlKey);
                }
            });
        });

        //Default sort to sort on username asc.
        if(gridReadyParams && gridReadyParams.api){
            gridReadyParams.api.applyColumnState({
                state: [{colId: "username", sort: "asc"}]
            })
        }

        await loadDataWithSSEAndStartChangeStreamListener("/sse/getStripeCustomerPaymentMethods", null,
            this.populateGrid, null, gridReadyParams, this.props.setSSEDataPullActive, null, null,
            this.abortController)
    };
    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="myGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            ClientSideRowModelModule,
                            AllCommunityModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        multiSortKey={"ctrl"}
                        columnDefs={this.props.columnDefs}
                        rowData={this.rowData}
                        onGridReady={this.onGridReady}
                        asyncTransactionWaitMillis={this.props.asyncTransactionWaitMillis}
                        suppressModelUpdateAfterUpdateTransaction={true}
                        //getRowId={this.getRowId}
                        onCellEditingStopped={this.onCellEditingStopped}
                        maintainColumnOrder={true} //fixes issue where if you re-order/move column then click anywhere on the grid it reverts this change
                        rowSelection={this.props.rowSelection}
                        onSelectionChanged={() => {}}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                    />
                </div>
            </div>
        );
    }
}
